import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Receipt,
  AccountBalance,
  TrendingUp,
  Add,
  FileDownload,
  CheckCircle,
  Schedule,
  Refresh,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../../config/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AccountingDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState({
    total_debit: 0,
    total_credit: 0,
    balance: 0,
    total_entries: 0,
    pending_entries: 0,
    total_accounts: 0,
    active_accounts: 0,
  });
  const [journalEntries, setJournalEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [generalLedger, setGeneralLedger] = useState([]);

  useEffect(() => {
    fetchSummary();
    if (tabValue === 0) {
      fetchJournalEntries();
    } else if (tabValue === 1) {
      fetchAccounts();
    } else if (tabValue === 2) {
      fetchGeneralLedger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const fetchSummary = async () => {
    try {
      setError('');
      const response = await api.get('/accounting/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load accounting summary';
      setError(errorMessage);
    }
  };

  const fetchJournalEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/accounting/journal-entries', {
        params: { limit: 50 }
      });
      setJournalEntries(response.data.items || []);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load journal entries';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/accounting/accounts', {
        params: { is_active: 'true' }
      });
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load accounts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralLedger = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/accounting/general-ledger');
      setGeneralLedger(response.data || []);
    } catch (error) {
      console.error('Error fetching general ledger:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load general ledger';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePostEntry = async (entryId) => {
    try {
      setError('');
      await api.post(`/accounting/journal-entries/${entryId}/post`);
      fetchJournalEntries();
      fetchSummary();
    } catch (error) {
      console.error('Error posting entry:', error);
      const errorMessage = error.response?.data?.message || 'Failed to post journal entry';
      setError(errorMessage);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatCurrency = (amount, symbol = '€') => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return symbol + '0.00';
    }
    return symbol + Number(amount).toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatAccountType = (type) => {
    if (!type) return '-';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Accounting</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchSummary();
              if (tabValue === 0) fetchJournalEntries();
              else if (tabValue === 1) fetchAccounts();
              else if (tabValue === 2) fetchGeneralLedger();
            }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => {
              // TODO: Navigate to journal entry creation form
              setError('Journal entry creation form not yet implemented');
            }}
          >
            New Entry
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalance sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Debit
                  </Typography>
                  <Typography variant="h6">{formatCurrency(summary.total_debit)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Receipt sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Credit
                  </Typography>
                  <Typography variant="h6">{formatCurrency(summary.total_credit)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Net Balance
                  </Typography>
                  <Typography variant="h6">{formatCurrency(summary.balance)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FileDownload sx={{ mr: 2, color: 'info.main' }} />
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Posted Entries
                  </Typography>
                  <Typography variant="h6">{summary.total_entries}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {summary.pending_entries} pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Journal Entries" />
            <Tab label="Chart of Accounts" />
            <Tab label="General Ledger" />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entry #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Total Debit</TableCell>
                  <TableCell align="right">Total Credit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journalEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary" sx={{ py: 4 }}>
                        {loading ? 'Loading...' : 'No journal entries found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  journalEntries.map((entry) => {
                    const totalDebit = entry.journal_lines?.reduce((sum, line) => sum + (line.debit_amount || 0), 0) || 0;
                    const totalCredit = entry.journal_lines?.reduce((sum, line) => sum + (line.credit_amount || 0), 0) || 0;
                    
                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell>{entry.entry_number}</TableCell>
                        <TableCell>{formatDate(entry.entry_date)}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                      <TableCell align="right">{formatCurrency(totalDebit, entry.currency?.symbol || '€')}</TableCell>
                      <TableCell align="right">{formatCurrency(totalCredit, entry.currency?.symbol || '€')}</TableCell>
                        <TableCell>
                          <Chip
                            label={entry.is_posted ? 'Posted' : 'Pending'}
                            color={entry.is_posted ? 'success' : 'warning'}
                            size="small"
                            icon={entry.is_posted ? <CheckCircle /> : <Schedule />}
                          />
                        </TableCell>
                        <TableCell>
                          {!entry.is_posted && (
                            <Tooltip title="Post Entry">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handlePostEntry(entry.id)}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Code</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="textSecondary" sx={{ py: 4 }}>
                        {loading ? 'Loading...' : 'No accounts found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <TableRow key={account.id} hover>
                    <TableCell>{account.account_code}</TableCell>
                    <TableCell>{account.account_name}</TableCell>
                    <TableCell>{formatAccountType(account.account_type)}</TableCell>
                    <TableCell>
                        <Chip
                          label={account.is_active ? 'Active' : 'Inactive'}
                          color={account.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Code</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell align="right">Total Debit</TableCell>
                  <TableCell align="right">Total Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generalLedger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary" sx={{ py: 4 }}>
                        {loading ? 'Loading...' : 'No ledger data found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  generalLedger.map((entry) => (
                    <TableRow key={entry.account_id} hover>
                      <TableCell>{entry.account_code}</TableCell>
                      <TableCell>{entry.account_name}</TableCell>
                      <TableCell align="right">{formatCurrency(entry.total_debit)}</TableCell>
                      <TableCell align="right">{formatCurrency(entry.total_credit)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={entry.balance >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(entry.balance)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
    </Box>
  );
};